import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Sidebar from "@/components/Sidebar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Menu,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Send
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Analytics } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PriceComparison() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [requestId, setRequestId] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const bundleId = params.get("bundle");
    if (bundleId) setSelectedBundle(bundleId);
    fetchData();
  }, [location.search]);

  const fetchData = async () => {
    try {
      const [packagesRes, bundlesRes] = await Promise.all([
        axios.get(`${API}/support-packages`, { withCredentials: true }),
        axios.get(`${API}/bundles`, { withCredentials: true })
      ]);
      setPackages(packagesRes.data.packages);
      setBundles(bundlesRes.data.bundles);
      
      // Don't auto-select service package - it's optional
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Track page view
  useEffect(() => {
    Analytics.comparisonPageViewed();
  }, []);

  // Track bundle selection
  const handleBundleSelect = (bundleId) => {
    const bundle = bundles.find(b => b.bundle_id === bundleId);
    if (bundle) {
      Analytics.bundleSelected(bundleId, bundle.name);
    }
    setSelectedBundle(selectedBundle === bundleId ? null : bundleId);
  };

  // Track package selection
  const handlePackageSelect = (packageId) => {
    const pkg = packages.find(p => p.package_id === packageId);
    if (pkg && selectedPackage !== packageId) {
      Analytics.packageSelected(packageId, pkg.name);
    }
    setSelectedPackage(selectedPackage === packageId ? null : packageId);
  };

  const handleRequestOffer = async () => {
    if (!selectedBundle) {
      toast.error("Please select a hardware bundle first");
      return;
    }

    Analytics.offerRequestStarted(selectedBundle);
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/offers/request`, {
        bundle_id: selectedBundle,
        support_package_id: selectedPackage || null
      }, { withCredentials: true });
      
      const bundle = bundles.find(b => b.bundle_id === selectedBundle);
      Analytics.offerRequestCompleted(
        selectedBundle, 
        bundle?.name, 
        bundle?.base_price, 
        !!selectedPackage
      );
      
      setRequestId(response.data.request_id);
      setShowThankYou(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit offer request");
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate estimated annual savings based on selected bundle
  const selectedBundleData = bundles.find(b => b.bundle_id === selectedBundle);
  const totalSavings = selectedBundleData 
    ? Math.round(selectedBundleData.market_price - selectedBundleData.base_price)
    : 35500;

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        currentPath={location.pathname}
      />

      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 glass border-b border-white/10">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 hover:bg-white/5 rounded-lg"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-heading text-xl font-semibold">Price Comparison & Savings</h1>
              </div>
            </div>
          </div>
        </header>

        <main className="p-6 space-y-8">
          {/* Main comparison card - Service Packages (Optional) */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading font-semibold text-lg">Add Support Package</h2>
                <p className="text-sm text-muted-foreground mt-1">Optional - You can skip this and request just the hardware bundle</p>
              </div>
              {selectedPackage && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedPackage(null)}
                  className="text-muted-foreground hover:text-white"
                >
                  Clear Selection
                </Button>
              )}
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="skeleton h-24 rounded-lg" />
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {packages.map((pkg, i) => (
                  <div
                    key={pkg.package_id}
                    onClick={() => setSelectedPackage(selectedPackage === pkg.package_id ? null : pkg.package_id)}
                    className={`relative border rounded-xl p-6 transition-all cursor-pointer ${
                      selectedPackage === pkg.package_id
                        ? "border-[#007AFF] bg-[#007AFF]/5 price-card" 
                        : pkg.is_recommended 
                          ? "border-[#00FF88]/50 bg-[#00FF88]/5 price-card recommended hover:border-[#00FF88]" 
                          : "border-border hover:border-white/20 price-card"
                    }`}
                    data-testid={`package-card-${pkg.package_id}`}
                  >
                    {pkg.is_recommended && (
                      <div className="absolute -top-3 right-4">
                        <span className="px-3 py-1 rounded-full recommended-badge text-xs font-medium">
                          Recommended
                        </span>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Selection indicator */}
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedPackage === pkg.package_id 
                            ? "border-[#007AFF] bg-[#007AFF]" 
                            : "border-muted-foreground"
                        }`}>
                          {selectedPackage === pkg.package_id && (
                            <div className="w-2 h-2 rounded-full bg-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-heading font-semibold text-lg">{pkg.name}</h3>
                          <p className="text-sm text-muted-foreground mt-1">{pkg.description}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <span className="text-2xl font-bold font-heading text-[#00FF88]">
                            AED {pkg.monthly_price}
                          </span>
                          <span className="text-muted-foreground">/month</span>
                        </div>

                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPackage(selectedPackage === pkg.package_id ? null : pkg.package_id);
                          }}
                          className={selectedPackage === pkg.package_id
                            ? "bg-[#007AFF] hover:bg-[#0055CC]"
                            : pkg.is_recommended 
                              ? "bg-[#00FF88] text-black hover:bg-[#00CC6A]"
                              : "bg-secondary hover:bg-secondary/80"
                          }
                          data-testid={`select-package-${pkg.package_id}`}
                        >
                          {selectedPackage === pkg.package_id ? "Selected" : "Select"}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Total Savings */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h2 className="font-heading font-semibold text-lg mb-4">Total Savings Against Market Rates</h2>

            <div className="border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold">Estimated Annual Savings</h3>
                  <p className="text-sm text-muted-foreground mt-1">Based on your selected bundle and market average</p>
                </div>
                <span className="text-4xl font-bold font-heading text-[#00FF88]">
                  AED {totalSavings.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* View TCO CTA */}
          <div className="flex justify-center">
            <Button
              size="lg"
              className="bg-[#A78BFA] hover:bg-[#9061F9] btn-hover-scale"
              onClick={() => navigate(`/tco${selectedBundle ? `?bundle=${selectedBundle}` : ''}`)}
              data-testid="view-tco-btn"
            >
              View TCO
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Hardware Bundles Comparison */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h2 className="font-heading font-semibold text-lg mb-6">Hardware Bundle Comparison</h2>

            {loading ? (
              <div className="grid md:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="skeleton h-48 rounded-lg" />
                ))}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {bundles.slice(0, 6).map((bundle) => (
                  <div
                    key={bundle.bundle_id}
                    className={`relative border rounded-xl p-5 cursor-pointer transition-all ${
                      selectedBundle === bundle.bundle_id
                        ? "border-[#007AFF] bg-[#007AFF]/5 ring-2 ring-[#007AFF]/20"
                        : "border-border hover:border-white/20"
                    }`}
                    onClick={() => handleBundleSelect(bundle.bundle_id)}
                    data-testid={`bundle-compare-${bundle.bundle_id}`}
                  >
                    {/* Selection checkmark */}
                    {selectedBundle === bundle.bundle_id && (
                      <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#007AFF] flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}

                    {bundle.is_recommended && !selectedBundle && (
                      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full recommended-badge text-xs font-medium mb-3">
                        <Sparkles className="w-3 h-3" />
                        Recommended
                      </div>
                    )}

                    <h3 className="font-heading font-semibold mb-2">{bundle.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{bundle.description}</p>

                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-xl font-bold font-heading">
                        AED {bundle.base_price.toLocaleString()}
                      </span>
                      <span className="text-sm text-muted-foreground line-through">
                        AED {bundle.market_price.toLocaleString()}
                      </span>
                    </div>

                    <div className="savings-highlight rounded px-2 py-1 inline-block">
                      <span className="text-xs text-[#00FF88]">
                        Save {Math.round((1 - bundle.base_price / bundle.market_price) * 100)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Request Offer Section */}
          <div className="bg-gradient-to-r from-[#007AFF]/10 to-[#00FF88]/10 border border-[#007AFF]/20 rounded-xl p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h2 className="font-heading font-semibold text-xl mb-2">Ready to proceed?</h2>
                <p className="text-muted-foreground">
                  {selectedBundle 
                    ? `You've selected a hardware bundle${selectedPackage ? ' and support package' : ''}. Request an offer to get a detailed quote.`
                    : 'Select a hardware bundle above to request an offer.'
                  }
                </p>
                {selectedBundle && (
                  <div className="mt-3 text-sm">
                    <span className="text-white font-medium">Selected: </span>
                    <span className="text-[#00FF88]">
                      {bundles.find(b => b.bundle_id === selectedBundle)?.name}
                      {selectedPackage && ` + ${packages.find(p => p.package_id === selectedPackage)?.name}`}
                    </span>
                  </div>
                )}
              </div>
              <Button
                size="lg"
                onClick={handleRequestOffer}
                disabled={!selectedBundle || submitting}
                className="bg-[#00FF88] text-black hover:bg-[#00CC6A] btn-hover-scale min-w-[200px] h-14 text-lg font-semibold"
                data-testid="request-offer-btn"
              >
                {submitting ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Request Offer
                  </>
                )}
              </Button>
            </div>
          </div>
        </main>
      </div>

      {/* Thank You Dialog */}
      <Dialog open={showThankYou} onOpenChange={setShowThankYou}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader className="text-center">
            <div className="w-16 h-16 mx-auto bg-[#00FF88]/10 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-[#00FF88]" />
            </div>
            <DialogTitle className="font-heading text-2xl">Thank You!</DialogTitle>
            <DialogDescription className="text-base mt-4">
              Your offer request has been submitted successfully.
              <br /><br />
              <span className="text-white font-medium">We'll be in touch within 12 hours</span> with a detailed quote tailored to your business needs.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 p-4 bg-secondary/50 rounded-lg text-sm text-center">
            <p className="text-muted-foreground">Reference ID</p>
            <p className="font-mono text-white">{requestId}</p>
          </div>
          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              className="flex-1 border-white/20"
              onClick={() => {
                setShowThankYou(false);
                navigate("/dashboard");
              }}
            >
              Back to Dashboard
            </Button>
            <Button
              className="flex-1 bg-[#007AFF] hover:bg-[#0055CC]"
              onClick={() => {
                setShowThankYou(false);
                setSelectedBundle(null);
                setSelectedPackage(null);
              }}
            >
              Request Another
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
